import { Alert, CheckboxGrid, Clickable, H1, P, TypeWrapper, useStepperContext, Wrapper } from '@ahmdigital/ui';
import { concat, includes, map, remove } from 'lodash/fp';
import { useSelector } from 'react-redux';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { analyticsLabels } from '../../../constants';
import { scrollTo } from '../../../../../utils/ui';
import { Service } from '../../../../../types/__codegen__/ahm-api/graphql';
import selectBuildYourCover from '../../../../../selectors/select-build-your-cover';
import StepFooterNavigation from '../../../components/step-footer-navigation';

type Props = {
  selectedServices: Service['serviceId'][];
  serviceList: Service[];
  setSelectedServices: (selectedServices: Service['serviceId'][]) => void;
  subtitle: string;
  title: string;
};

export const AlertSection = styled.div`
  text-align: left;

  @media (min-width: ${({ theme }) => theme.media.md}) {
    width: fit-content;
  }
`;

const StyledClickable = styled(Clickable)`
  margin: 2.5rem 0;
`;

const initialViewCount = 10;
const revealCount = 10;

const ServiceSelection = ({ selectedServices, serviceList, setSelectedServices, subtitle, title }: Props) => {
  // @ts-expect-error [🤖 PLEASE FIX 🤖]: Property 'activeStep' does not exist on type '{}'.
  const { activeStep, goToNextStep } = useStepperContext();
  const [selections, setSelections] = useState(selectedServices);
  const { selectedProducts } = useSelector(selectBuildYourCover);
  const [toView, setToView] = useState(initialViewCount);
  const [showAlert, setShowAlert] = useState(Boolean(selectedProducts.hospital) || Boolean(selectedProducts.extras));

  useEffect(() => {
    setShowAlert(Boolean(selectedProducts.hospital) || Boolean(selectedProducts.extras));
  }, [selectedProducts]);

  const handleCloseAlert = () => setShowAlert(false);

  const toggleSelection = (service: string) => {
    if (includes(service, selections)) {
      setSelections(remove((selection) => selection === service, selections));
    } else {
      setSelections(concat([service], selections));
    }
  };

  const viewMore = () => {
    setToView(Math.min(toView + revealCount, serviceList.length));
  };
  const backToTop = () => {
    scrollTo(0);
  };

  const handleOnNextClick = () => {
    setSelectedServices(selections);
    goToNextStep();
  };

  const items = map(
    (service: Service) => ({
      analytics: {
        label: `Selected ${analyticsLabels[activeStep]} service`,
      },
      label: service.name,
      tooltipContent: service.description,
      value: service.serviceId,
    }),
    serviceList,
  );

  return (
    <>
      <Wrapper size="medium">
        <TypeWrapper variant="center">
          <H1 looksLike="h2">{title}</H1>
          <P variant="paddingBottomSpacer8">{subtitle}</P>
          <AlertSection>
            {showAlert && (
              <Alert
                heading="Your cover may change"
                analyticsLabel="Cover may change"
                level="info"
                onClose={handleCloseAlert}
              >
                <P>Updating your hospital or extras services may remove some of your previously selected cover.</P>
              </Alert>
            )}
          </AlertSection>
          {/* @ts-expect-error [🤖 PLEASE FIX 🤖]: Type '{ analytics: { label: string; }; label: string; tooltipContent: Maybe<string> | undefined; value: string; }[]' is not assignable to type 'Items[]'. */}
          <CheckboxGrid checkedValues={selections} items={items.slice(0, toView)} onChange={toggleSelection} />

          {toView < serviceList.length ? (
            <StyledClickable onClick={viewMore} variant="button" icon={{ name: 'chevron-down', position: 'right' }}>
              View more
            </StyledClickable>
          ) : (
            <StyledClickable onClick={backToTop} variant="button" icon={{ name: 'chevron-up', position: 'right' }}>
              Back to top
            </StyledClickable>
          )}
        </TypeWrapper>
      </Wrapper>
      <StepFooterNavigation onNextClick={handleOnNextClick} />
    </>
  );
};

export default ServiceSelection;
